"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Mail, Phone, MapPin, Shield, CircleCheck as CheckCircle2, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const termsSections = [{
  id: "introduction",
  number: "01",
  title: "Introduction and acceptance of terms",
  content: "These Terms of Service govern your access to this website and set out the basis on which I, Yassine Essaadi operating as YE Consultancy, provide independent freelance search engine optimisation (SEO) consultancy services. By accessing this website or engaging my services, you acknowledge that you have read, understood, and agreed to be bound by these terms. If you do not agree with any part of these terms, please discontinue using this site immediately."
}, {
  id: "website-use",
  number: "02",
  title: "Use of the website",
  content: "You agree to use this website solely for lawful purposes and in a manner that does not infringe the rights of, restrict, or inhibit the use and enjoyment of the site by any other party. You must not attempt to compromise the integrity, security, or availability of the website, introduce malicious software, or attempt unauthorised access to any server, database, or network connected to YE Consultancy."
}, {
  id: "services-description",
  number: "03",
  title: "Description of consultancy services",
  content: "YE Consultancy delivers bespoke SEO consulting directly through me, Yassine Essaadi. Services include technical SEO audits, search strategy development, on-page optimisation, national and international SEO planning, B2B organic lead generation consulting, and AI search optimisation. Because search engine algorithms and web environments evolve continuously, I provide expert advisory and implementation recommendations based on industry best practice; I do not guarantee specific numerical search rankings, indexation timelines, or traffic quotas."
}, {
  id: "intellectual-property",
  number: "04",
  title: "Intellectual property rights",
  content: "All original content, branding, design elements, articles, methodologies, and framework documentation published on this website remain the exclusive intellectual property of YE Consultancy. Upon receipt of full payment for agreed client engagements, you receive an irrevocable, non-exclusive licence to use custom deliverables, audits, and strategic reports produced specifically for your business."
}, {
  id: "client-responsibilities",
  number: "05",
  title: "Client responsibilities",
  content: "Successful SEO consulting depends on clear communication and timely cooperation. You agree to provide accurate business information, prompt feedback on proposals, and necessary administrative access to Google Search Console, analytics platforms, content management systems, or development staging environments where required for agreed project execution."
}, {
  id: "liability",
  number: "06",
  title: "Limitation of liability",
  content: "To the maximum extent permitted by applicable law, YE Consultancy will not be liable for any indirect, incidental, special, or consequential loss, loss of profits, business interruption, or data loss arising from search engine algorithm updates, third-party hosting actions, or changes implemented without direct consultation. In all circumstances, total cumulative liability regarding any engagement shall not exceed the total fees paid by you for the specific service in dispute during the preceding three-month period."
}, {
  id: "payment-terms",
  number: "07",
  title: "Payment and engagement terms",
  content: "Engagements are confirmed in writing via an agreed scope of work or project agreement. Invoices are issued in British Pounds (GBP) and are payable within the timeframe indicated on each invoice (typically 14 calendar days). For monthly retainer agreements, fees are invoiced in advance. Work may be paused if outstanding invoices remain unpaid past their due date."
}, {
  id: "termination",
  number: "08",
  title: "Termination of services",
  content: "Either party may terminate an ongoing consultancy agreement by providing written notice in accordance with the notice period specified in the individual statement of work (typically 30 days for ongoing monthly retainers). In the event of termination, the client remains responsible for payment for all work performed and time accrued up to the effective termination date."
}, {
  id: "governing-law",
  number: "09",
  title: "Governing law and jurisdiction",
  content: "These terms and any dispute or claim arising out of or in connection with them or their subject matter or formation (including non-contractual disputes or claims) shall be governed by and construed in accordance with the laws of England and Wales. Both parties agree that the courts of England and Wales shall have exclusive jurisdiction to settle any dispute."
}];
export default function Terms() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="termsofservice" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        {/* Header Block */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4 font-medium">
            Legal agreement
          </Badge>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-foreground">
            Terms of service
          </h1>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            Clear guidelines on how we work together, what to expect from my consultancy, and our shared responsibilities.
          </p>
          <div className="mt-4 inline-flex items-center gap-2 text-xs sm:text-sm text-muted-foreground font-sans">
            <span className="inline-block w-2 h-2 rounded-full bg-accent" />
            <span>Last updated: March 2025</span>
            <span>•</span>
            <span>Direct consultation with Yassine Essaadi</span>
          </div>
        </div>

        {/* Quick Table of Contents / Summary Card */}
        <Card className="mb-12 border border-border bg-card/60 shadow-xs">
          <CardContent className="p-6 md:p-8">
            <div className="flex items-start gap-4">
              <div className="p-2.5 rounded-lg bg-primary/10 text-primary shrink-0 mt-0.5">
                <Shield className="size-5" />
              </div>
              <div className="space-y-2">
                <h2 className="font-serif text-lg font-semibold text-foreground">
                  Key points at a glance
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed font-sans">
                  I operate as an independent freelance SEO consultant based in Swindon, UK. You work directly with me,
                  receiving tailored strategic advice and bespoke audits. I adhere strictly to transparent standards,
                  industry best practices, and English commercial law.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Terms Clauses List */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }} className="space-y-8">
          {termsSections.map((section, index) => <motion.article key={section.id} data-index={index} variants={itemVariants} className="group rounded-xl border border-border/70 bg-card p-6 md:p-8 transition-colors hover:border-primary/40 shadow-xs">
              <div className="flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-4 mb-3">
                <span className="font-mono text-xs md:text-sm font-semibold text-primary/80 tracking-wider">
                  Section {section.number}
                </span>
                <h2 className="font-serif text-xl md:text-2xl font-semibold text-foreground tracking-tight">
                  {section.title}
                </h2>
              </div>
              <p className="font-sans text-sm md:text-base text-muted-foreground leading-relaxed">
                {section.content}
              </p>
            </motion.article>)}
        </motion.div>

        <Separator className="my-12 md:my-16" />

        {/* Section 10: Contact details */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="rounded-2xl border border-border bg-card p-8 md:p-10 shadow-sm">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <span className="font-mono text-xs md:text-sm font-semibold text-primary/80 tracking-wider">
                Section 10
              </span>
              <h2 className="font-serif text-2xl font-semibold text-foreground tracking-tight">
                Contact and company information
              </h2>
            </div>
            <p className="font-sans text-sm md:text-base text-muted-foreground leading-relaxed mb-6">
              If you have any questions regarding these terms, your existing contract, or a prospective SEO consultation,
              please reach out to me directly through the details below.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-md bg-secondary text-secondary-foreground shrink-0 mt-0.5">
                  <Mail className="size-4 text-primary" />
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
                    Email
                  </p>
                  <a href="mailto:contact@ye-consultancy.com" className="text-sm font-medium text-foreground hover:text-primary transition-colors underline-offset-4 hover:underline">
                    contact@ye-consultancy.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="p-2 rounded-md bg-secondary text-secondary-foreground shrink-0 mt-0.5">
                  <Phone className="size-4 text-primary" />
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
                    Phone
                  </p>
                  <a href="tel:+447490984430" className="text-sm font-medium text-foreground hover:text-primary transition-colors underline-offset-4 hover:underline">
                    +44 7490984430
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="p-2 rounded-md bg-secondary text-secondary-foreground shrink-0 mt-0.5">
                  <MapPin className="size-4 text-primary" />
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
                    Office location
                  </p>
                  <p className="text-sm font-medium text-foreground leading-snug">
                    Windmill Hill Business Park, Whitehill Way, Swindon SN5 6QR, UK
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-border/80 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>Sole practitioner: Yassine Essaadi, t/a YE Consultancy</span>
              </div>
              <Button asChild variant="default" size="default">
                <Link to="/ContactPage">
                  Book a consultation
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}